/**
 * Push Notification Service - Vendor App
 * Initializes Firebase Cloud Messaging and handles push notifications
 */

import { initializeApp, getApps } from 'firebase/app';
import { getMessaging, getToken, onMessage, Messaging } from 'firebase/messaging';

const VAPID_KEY = import.meta.env.VITE_FCM_VAPID_KEY;
const API_BASE = import.meta.env.VITE_API_URL || 'http://localhost:3000';

const firebaseConfig = {
  apiKey: import.meta.env.VITE_FIREBASE_API_KEY,
  authDomain: import.meta.env.VITE_FIREBASE_AUTH_DOMAIN,
  projectId: import.meta.env.VITE_FIREBASE_PROJECT_ID,
  storageBucket: import.meta.env.VITE_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: import.meta.env.VITE_FIREBASE_MESSAGING_SENDER_ID,
  appId: import.meta.env.VITE_FIREBASE_APP_ID,
};

let messaging: Messaging | null = null;

/**
 * Check if push notifications are supported
 */
export function isPushSupported(): boolean {
  return 'serviceWorker' in navigator && 'PushManager' in window && 'Notification' in window;
}

/**
 * Initialize Firebase and request notification permission
 */
export async function initPushNotifications(): Promise<string | null> {
  if (!isPushSupported()) {
    console.warn('Push notifications not supported in this browser');
    return null;
  }

  // Check if Firebase config is available
  if (!firebaseConfig.apiKey || !firebaseConfig.projectId) {
    console.warn('Firebase config not found - push notifications disabled');
    return null;
  }

  try {
    // Initialize Firebase app (only once)
    const app = getApps().length === 0 ? initializeApp(firebaseConfig) : getApps()[0];
    messaging = getMessaging(app);

    // Request permission
    const permission = await Notification.requestPermission();
    if (permission !== 'granted') {
      console.warn('Notification permission denied');
      return null;
    }

    // Register service worker
    const registration = await navigator.serviceWorker.register('/firebase-messaging-sw.js');
    console.log('Service worker registered:', registration.scope);

    // Get FCM token
    const token = await getToken(messaging, {
      vapidKey: VAPID_KEY,
      serviceWorkerRegistration: registration,
    });

    if (token) {
      console.log('FCM Token obtained:', token.substring(0, 20) + '...');
      // Register token with backend
      await registerTokenWithBackend(token);
      return token;
    } else {
      console.warn('No FCM token available');
      return null;
    }
  } catch (error) {
    console.error('Failed to initialize push notifications:', error);
    return null;
  }
}

/**
 * Get or create a unique device ID
 */
function getDeviceId(): string {
  let deviceId = localStorage.getItem('push_device_id');
  if (!deviceId) {
    deviceId = 'web_' + Math.random().toString(36).substring(2, 15) + Date.now().toString(36);
    localStorage.setItem('push_device_id', deviceId);
  }
  return deviceId;
}

/**
 * Register FCM token with backend API
 */
async function registerTokenWithBackend(token: string): Promise<void> {
  try {
    const authToken = localStorage.getItem('accessToken');
    if (!authToken) {
      console.warn('No auth token - cannot register push token');
      return;
    }

    const response = await fetch(`${API_BASE}/api/notifications/register`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${authToken}`,
      },
      body: JSON.stringify({
        deviceId: getDeviceId(),
        role: 'rider',
        platform: 'web',
        pushToken: token,
      }),
    });

    if (response.ok) {
      console.log('Push token registered with backend');
    } else {
      const error = await response.text();
      console.warn('Failed to register push token:', response.status, error);
    }
  } catch (error) {
    console.error('Failed to register push token with backend:', error);
  }
}

/**
 * Listen for foreground messages
 */
export function onForegroundMessage(callback: (payload: any) => void): () => void {
  if (!messaging) {
    console.warn('Messaging not initialized');
    return () => {};
  }

  return onMessage(messaging, (payload) => {
    console.log('Foreground message received:', payload);
    callback(payload);
  });
}
